/**
 * Op helpers shared by client and server.
 *
 * An "op" is one persisted drawing action. Strokes store their points as a flat
 * `[x, y, pressure, x, y, pressure, ...]` array — roughly 3x smaller over the
 * wire than an array of objects, which matters when you are streaming points at
 * pointer-event rate to four other people.
 */

import { OP, TOOLS, LIMITS } from './protocol.js';
import { isShape } from './shapes.js';

const num = (v, fallback = 0) => (Number.isFinite(v) ? v : fallback);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Half-width the stroke actually covers, used to pad bounding boxes. */
export function strokeRadius(op) {
  const size = num(op.size, 4);
  if (op.tool === TOOLS.HIGHLIGHTER) return size * 1.6;
  if (op.tool === TOOLS.NEON) return size * 1.8;
  return size * 0.6;
}

/** Axis-aligned world-space bounds of an op, padded for its stroke width. */
export function opBounds(op) {
  switch (op.kind) {
    case OP.STROKE: {
      const pts = op.points || [];
      if (pts.length < 2) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
      let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
      for (let i = 0; i < pts.length; i += 3) {
        const x = pts[i]; const y = pts[i + 1];
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
      const r = strokeRadius(op) + 1;
      return { minX: minX - r, minY: minY - r, maxX: maxX + r, maxY: maxY + r };
    }
    case OP.SHAPE: {
      const r = num(op.size, 4) * 0.6 + 1;
      return {
        minX: Math.min(op.x0, op.x1) - r,
        minY: Math.min(op.y0, op.y1) - r,
        maxX: Math.max(op.x0, op.x1) + r,
        maxY: Math.max(op.y0, op.y1) + r,
      };
    }
    case OP.STAMP: {
      const r = num(op.size, 48) * 0.75;
      return { minX: op.x - r, minY: op.y - r, maxX: op.x + r, maxY: op.y + r };
    }
    case OP.TEXT: {
      const size = num(op.size, 24);
      const width = Math.max(1, String(op.text || '').length) * size * 0.62;
      return { minX: op.x, minY: op.y - size, maxX: op.x + width, maxY: op.y + size * 0.35 };
    }
    case OP.FILL: {
      let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
      for (const ring of op.rings || []) {
        for (let i = 0; i < ring.length; i += 2) {
          if (ring[i] < minX) minX = ring[i];
          if (ring[i] > maxX) maxX = ring[i];
          if (ring[i + 1] < minY) minY = ring[i + 1];
          if (ring[i + 1] > maxY) maxY = ring[i + 1];
        }
      }
      if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
      return { minX, minY, maxX, maxY };
    }
    case OP.IMAGE:
      return { minX: op.x, minY: op.y, maxX: op.x + num(op.w, 1), maxY: op.y + num(op.h, 1) };
    default:
      return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }
}

/** Union of two bounds boxes; either may be null. */
export function unionBounds(a, b) {
  if (!a) return b ? { ...b } : null;
  if (!b) return { ...a };
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

export function boundsIntersect(a, b) {
  return !(a.maxX < b.minX || a.minX > b.maxX || a.maxY < b.minY || a.minY > b.maxY);
}

const HEX = /^#[0-9a-fA-F]{6}$/;
const sanitizeColor = (c) => (HEX.test(String(c)) ? String(c) : '#111418');

const KNOWN_TOOLS = new Set(Object.values(TOOLS));

/**
 * Normalise and bound-check an op arriving from a client. Returns the cleaned
 * op, or throws with a short reason. Never trust the wire.
 */
export function sanitizeOp(raw) {
  if (!raw || typeof raw !== 'object') throw new Error('op must be an object');
  const kind = raw.kind;
  const base = {
    id: typeof raw.id === 'string' && raw.id.length <= 64 ? raw.id : null,
    kind,
  };
  if (!base.id) throw new Error('op id missing');

  switch (kind) {
    case OP.STROKE: {
      const pts = Array.isArray(raw.points) ? raw.points : [];
      if (pts.length < 3) throw new Error('stroke needs at least one point');
      if (pts.length > LIMITS.maxPointsPerStroke * 3) throw new Error('stroke too long');
      const points = new Array(pts.length - (pts.length % 3));
      for (let i = 0; i < points.length; i += 1) {
        const v = Number(pts[i]);
        if (!Number.isFinite(v)) throw new Error('stroke has a non-finite point');
        points[i] = i % 3 === 2 ? clamp(v, 0, 1) : clamp(v, -1e9, 1e9);
      }
      return {
        ...base,
        tool: KNOWN_TOOLS.has(raw.tool) ? raw.tool : TOOLS.PEN,
        color: sanitizeColor(raw.color),
        size: clamp(num(raw.size, 4), 0.2, 400),
        opacity: clamp(num(raw.opacity, 1), 0.02, 1),
        smooth: raw.smooth !== false,
        points,
      };
    }
    case OP.SHAPE:
      return {
        ...base,
        shape: isShape(raw.shape) ? raw.shape : 'line',
        color: sanitizeColor(raw.color),
        fill: raw.fill ? sanitizeColor(raw.fill) : null,
        size: clamp(num(raw.size, 4), 0.2, 400),
        opacity: clamp(num(raw.opacity, 1), 0.02, 1),
        x0: clamp(num(raw.x0), -1e9, 1e9),
        y0: clamp(num(raw.y0), -1e9, 1e9),
        x1: clamp(num(raw.x1), -1e9, 1e9),
        y1: clamp(num(raw.y1), -1e9, 1e9),
      };
    case OP.STAMP:
      return {
        ...base,
        char: String(raw.char ?? '⭐').slice(0, 8),
        x: clamp(num(raw.x), -1e9, 1e9),
        y: clamp(num(raw.y), -1e9, 1e9),
        size: clamp(num(raw.size, 48), 4, 4000),
        rotation: clamp(num(raw.rotation, 0), -Math.PI * 2, Math.PI * 2),
      };
    case OP.TEXT: {
      const text = String(raw.text ?? '').slice(0, LIMITS.maxTextLength);
      if (!text.trim()) throw new Error('text is empty');
      return {
        ...base,
        text,
        x: clamp(num(raw.x), -1e9, 1e9),
        y: clamp(num(raw.y), -1e9, 1e9),
        size: clamp(num(raw.size, 24), 4, 2000),
        color: sanitizeColor(raw.color),
        font: raw.font === 'mono' ? 'mono' : 'sans',
      };
    }
    case OP.IMAGE: {
      const src = String(raw.src ?? '');
      if (!src.startsWith('data:image/')) throw new Error('image must be a data URL');
      if (src.length > LIMITS.maxImageBytes) throw new Error('image too large');
      return {
        ...base,
        src,
        x: clamp(num(raw.x), -1e9, 1e9),
        y: clamp(num(raw.y), -1e9, 1e9),
        w: clamp(num(raw.w, 1), 1, 1e7),
        h: clamp(num(raw.h, 1), 1, 1e7),
        opacity: clamp(num(raw.opacity, 1), 0.02, 1),
      };
    }
    case OP.FILL: {
      // A fill arrives as the outlines the flood produced: one ring per
      // boundary, holes included. Even-odd winding turns them back into the
      // filled region without needing to know which ring is which.
      const rings = Array.isArray(raw.rings) ? raw.rings : [];
      if (!rings.length) throw new Error('fill has no outline');
      if (rings.length > LIMITS.maxFillRings) throw new Error('fill too intricate');
      let total = 0;
      const out = [];
      for (const ring of rings) {
        if (!Array.isArray(ring) || ring.length < 6) continue;
        const len = ring.length - (ring.length % 2);
        total += len / 2;
        if (total > LIMITS.maxFillPoints) throw new Error('fill too intricate');
        const clean = new Array(len);
        for (let i = 0; i < len; i += 1) {
          const v = Number(ring[i]);
          if (!Number.isFinite(v)) throw new Error('fill has a non-finite point');
          clean[i] = clamp(v, -1e9, 1e9);
        }
        out.push(clean);
      }
      if (!out.length) throw new Error('fill has no usable outline');
      return {
        ...base,
        rings: out,
        color: sanitizeColor(raw.color),
        opacity: clamp(num(raw.opacity, 1), 0.02, 1),
      };
    }
    default:
      throw new Error(`unknown op kind: ${kind}`);
  }
}

/** True when a tool removes pixels rather than adding them. */
export function isEraser(tool) {
  return tool === TOOLS.ERASER;
}

/** Op kinds that came from a file rather than from someone's hand. */
export function isImported(op) {
  return op.kind === OP.IMAGE;
}

/**
 * Map an op's geometry from one bounding box onto another.
 *
 * Returns only the fields that changed, so moving a photo sends four numbers
 * rather than the whole data URL back over the wire. Line and font sizes scale
 * by the geometric mean of the two axes, which keeps a stroke looking right
 * even when the box is stretched unevenly.
 */
export function transformGeometry(op, fromBox, toBox) {
  const fw = fromBox.maxX - fromBox.minX;
  const fh = fromBox.maxY - fromBox.minY;
  // Degenerate source boxes (a single dot, a perfectly straight line) would
  // divide by zero; fall back to pure translation on that axis.
  const sx = Math.abs(fw) < 1e-9 ? 1 : (toBox.maxX - toBox.minX) / fw;
  const sy = Math.abs(fh) < 1e-9 ? 1 : (toBox.maxY - toBox.minY) / fh;

  const mapX = (x) => toBox.minX + (x - fromBox.minX) * sx;
  const mapY = (y) => toBox.minY + (y - fromBox.minY) * sy;
  const scale = Math.sqrt(Math.abs(sx * sy)) || 1;

  switch (op.kind) {
    case OP.STROKE: {
      const points = op.points.slice();
      for (let i = 0; i < points.length; i += 3) {
        points[i] = mapX(points[i]);
        points[i + 1] = mapY(points[i + 1]);
      }
      return { points, size: op.size * scale };
    }
    case OP.SHAPE:
      return {
        x0: mapX(op.x0),
        y0: mapY(op.y0),
        x1: mapX(op.x1),
        y1: mapY(op.y1),
        size: op.size * scale,
      };
    case OP.STAMP:
      return { x: mapX(op.x), y: mapY(op.y), size: op.size * scale };
    case OP.TEXT:
      return { x: mapX(op.x), y: mapY(op.y), size: op.size * scale };
    case OP.IMAGE:
      return {
        x: mapX(op.x),
        y: mapY(op.y),
        w: Math.max(1e-3, op.w * sx),
        h: Math.max(1e-3, op.h * sy),
      };
    case OP.FILL: {
      const rings = op.rings.map((ring) => {
        const next = ring.slice();
        for (let i = 0; i < next.length; i += 2) {
          next[i] = mapX(next[i]);
          next[i + 1] = mapY(next[i + 1]);
        }
        return next;
      });
      return { rings };
    }
    default:
      return {};
  }
}

/** The geometry fields of an op, for remembering what to undo back to. */
export function geometryOf(op) {
  switch (op.kind) {
    case OP.STROKE:
      return { points: op.points.slice(), size: op.size };
    case OP.SHAPE:
      return { x0: op.x0, y0: op.y0, x1: op.x1, y1: op.y1, size: op.size };
    case OP.STAMP:
    case OP.TEXT:
      return { x: op.x, y: op.y, size: op.size };
    case OP.IMAGE:
      return { x: op.x, y: op.y, w: op.w, h: op.h };
    case OP.FILL:
      return { rings: op.rings.map((ring) => ring.slice()) };
    default:
      return {};
  }
}

/** Whether an op keeps its proportions when resized from a corner. */
export function preservesAspectByDefault(op) {
  return op.kind === OP.IMAGE || op.kind === OP.STAMP || op.kind === OP.TEXT;
}

/** Douglas–Peucker simplification on the flat point array, for storage. */
export function simplifyPoints(points, tolerance = 0.6) {
  const n = points.length / 3;
  if (n < 3) return points;

  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack = [[0, n - 1]];

  while (stack.length) {
    const [first, last] = stack.pop();
    if (last - first < 2) continue;
    const ax = points[first * 3];
    const ay = points[first * 3 + 1];
    const bx = points[last * 3];
    const by = points[last * 3 + 1];
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;

    let maxDist = -1;
    let maxIdx = -1;
    for (let i = first + 1; i < last; i += 1) {
      const px = points[i * 3];
      const py = points[i * 3 + 1];
      let dist;
      if (lenSq === 0) {
        dist = (px - ax) ** 2 + (py - ay) ** 2;
      } else {
        const t = clamp(((px - ax) * dx + (py - ay) * dy) / lenSq, 0, 1);
        dist = (px - (ax + t * dx)) ** 2 + (py - (ay + t * dy)) ** 2;
      }
      if (dist > maxDist) { maxDist = dist; maxIdx = i; }
    }

    if (maxDist > tolerance * tolerance && maxIdx > 0) {
      keep[maxIdx] = 1;
      stack.push([first, maxIdx], [maxIdx, last]);
    }
  }

  const out = [];
  for (let i = 0; i < n; i += 1) {
    if (keep[i]) out.push(points[i * 3], points[i * 3 + 1], points[i * 3 + 2]);
  }
  return out;
}
